import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import LoadingComp from '../common/loading.js';
import { getAllowedDomains, getDashboardLinks, saveAllowedDomains, saveDashboardLinks } from './sysManagerGeneralSettingsApi.js';
import './SysManagerGeneralSettings.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

/** Convert one domain per line into a normalised, unique list for saving. */
function parseDomains(value) {
    return [...new Set(value.split(/\r?\n/).map(domain => domain.trim().toLowerCase().replace(/^@/, '').replace(/^\./, '')).filter(Boolean))];
}

/** Create one client-only identity so links can be edited and reordered safely. */
function editableLinks(values) {
    return values.map((link, index) => ({ id: `dashboard-link-${Date.now()}-${index}`, text: link.text, link: link.link, image: link.image }));
}

/** Guard the school-wide settings area with its dedicated System Manager flag. */
export default function SysManagerGeneralSettings() {
    const permissions = userAppData?.sysManager || {};
    const authorised = Number(permissions.perms ?? 0) === 1 && permissions.generalAdmin === true;
    const [domains, setDomains] = useState([]);
    const [draft, setDraft] = useState('');
    const [domainsStatus, setDomainsStatus] = useState('loading');
    const [dashboardLinks, setDashboardLinks] = useState([]);
    const [dashboardDraft, setDashboardDraft] = useState([]);
    const [dashboardStatus, setDashboardStatus] = useState('loading');
    const [modalOpen, setModalOpen] = useState(false);
    const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!authorised) return undefined;
        let active = true;
        getAllowedDomains().then(values => { if (active) { setDomains(values); setDomainsStatus('ready'); } })
            .catch(() => { if (active) setDomainsStatus('error'); });
        getDashboardLinks().then(values => { if (active) { setDashboardLinks(values); setDashboardStatus('ready'); } })
            .catch(() => { if (active) setDashboardStatus('error'); });
        return () => { active = false; };
    }, [authorised]);

    function openEditor() {
        setDraft(domains.join('\n'));
        setModalOpen(true);
    }

    async function save() {
        const values = parseDomains(draft);
        setSaving(true);
        try {
            const saved = await saveAllowedDomains(values);
            setDomains(saved);
            setModalOpen(false);
            toast.success('✅ Approved domains updated.', TOAST_CONFIG);
        } catch (error) {
            toast.error(`❌ ${error.message || 'Approved domains could not be updated.'}`, TOAST_CONFIG);
        } finally {
            setSaving(false);
        }
    }

    function openDashboardEditor() {
        setDashboardDraft(editableLinks(dashboardLinks));
        setDashboardModalOpen(true);
    }

    function updateDashboardLink(id, field, value) {
        setDashboardDraft(current => current.map(item => item.id === id ? { ...item, [field]: value } : item));
    }

    function moveDashboardLink(index, offset) {
        setDashboardDraft(current => {
            const target = index + offset;
            if (target < 0 || target >= current.length) return current;
            const next = [...current];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    }

    async function saveDashboard() {
        const values = dashboardDraft.map(({ text, link, image }) => ({ text: text.trim(), link: link.trim(), image: image.trim() }));
        setSaving(true);
        try {
            const saved = await saveDashboardLinks(values);
            setDashboardLinks(saved);
            setDashboardModalOpen(false);
            toast.success('✅ Dashboard links updated.', TOAST_CONFIG);
        } catch (error) {
            toast.error(`❌ ${error.message || 'Dashboard links could not be updated.'}`, TOAST_CONFIG);
        } finally {
            setSaving(false);
        }
    }

    if (!authorised) return <Navigate to='/app/sysmanager' replace />;

    return <div className='main_div'><div className='internal_main_div general-settings-container'>
        <Header
            title='General Settings'
            subtitle='Manage school-wide settings used across School Engine.'
            rightContent={<Link className='module-page-header__button' to='/app/sysmanager'>Back to System Manager</Link>}
        />
        {domainsStatus === 'loading' && dashboardStatus === 'loading' ? <LoadingComp /> : <section className='general-settings-grid'>
            <button className='general-settings-tile' type='button' disabled={domainsStatus !== 'ready'} onClick={openEditor}>
                <span className='general-settings-tile-name'>Approved Domains</span>
                <span className='general-settings-tile-description'>{domainsStatus === 'error' ? 'The domain list could not be loaded.' : `Manage the domains approved for restricted Bulk Email users. ${domains.length} configured.`}</span>
            </button>
            <button className='general-settings-tile' type='button' disabled={dashboardStatus !== 'ready'} onClick={openDashboardEditor}>
                <span className='general-settings-tile-name'>Dashboard Links</span>
                <span className='general-settings-tile-description'>{dashboardStatus === 'error' ? 'The dashboard links could not be loaded.' : `Manage school-wide dashboard shortcuts. ${dashboardLinks.length} configured.`}</span>
            </button>
        </section>}
        {modalOpen && <div className='general-settings-modal-backdrop' role='presentation' onMouseDown={event => { if (event.target === event.currentTarget && !saving) setModalOpen(false); }}>
            <section className='general-settings-modal' role='dialog' aria-modal='true' aria-labelledby='allowed-domains-title'>
                <button className='general-settings-modal-close' type='button' aria-label='Close approved domains' disabled={saving} onClick={() => setModalOpen(false)}>×</button>
                <h2 id='allowed-domains-title'>Approved Domains</h2>
                <p>Enter one domain per line without an email address, for example <strong>school.org.uk</strong>. Subdomains are also accepted by Bulk Email.</p>
                <label><span>Domains</span><textarea rows='12' value={draft} disabled={saving} onChange={event => setDraft(event.target.value)} placeholder={'school.org.uk\nacademy.example'} /></label>
                <div className='general-settings-modal-actions'><button type='button' disabled={saving} onClick={() => setModalOpen(false)}>Cancel</button><button className='general-settings-primary' type='button' disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save domains'}</button></div>
            </section>
        </div>}
        {dashboardModalOpen && <div className='general-settings-modal-backdrop' role='presentation' onMouseDown={event => { if (event.target === event.currentTarget && !saving) setDashboardModalOpen(false); }}>
            <section className='general-settings-modal general-settings-modal--wide' role='dialog' aria-modal='true' aria-labelledby='dashboard-links-title'>
                <button className='general-settings-modal-close' type='button' aria-label='Close dashboard links' disabled={saving} onClick={() => setDashboardModalOpen(false)}>×</button>
                <div className='general-settings-modal-heading'><div><h2 id='dashboard-links-title'>Dashboard Links</h2><p>Edit the school-wide shortcuts shown on every user's dashboard.</p></div><button type='button' disabled={saving} onClick={() => setDashboardDraft(current => [...current, { id: `dashboard-link-${Date.now()}`, text: '', link: 'https://', image: 'https://' }])}>Add link</button></div>
                <div className='general-settings-link-list'>{dashboardDraft.map((item, index) => <section className='general-settings-link-editor' key={item.id}>
                    <div className='general-settings-link-heading'><strong>Link {index + 1}</strong><div><button type='button' disabled={saving || index === 0} onClick={() => moveDashboardLink(index, -1)}>Move up</button><button type='button' disabled={saving || index === dashboardDraft.length - 1} onClick={() => moveDashboardLink(index, 1)}>Move down</button><button className='general-settings-danger' type='button' disabled={saving} onClick={() => setDashboardDraft(current => current.filter(link => link.id !== item.id))}>Remove</button></div></div>
                    <div className='general-settings-link-fields'><label><span>Display text</span><input type='text' maxLength='80' value={item.text} disabled={saving} onChange={event => updateDashboardLink(item.id, 'text', event.target.value)} /></label><label><span>Destination URL</span><input type='url' maxLength='2000' value={item.link} disabled={saving} onChange={event => updateDashboardLink(item.id, 'link', event.target.value)} /></label><label><span>Image URL</span><input type='url' maxLength='2000' value={item.image} disabled={saving} onChange={event => updateDashboardLink(item.id, 'image', event.target.value)} /></label>{item.image && item.image !== 'https://' && <img src={item.image} alt='' onError={event => { event.currentTarget.style.visibility = 'hidden'; }} onLoad={event => { event.currentTarget.style.visibility = 'visible'; }} />}</div>
                </section>)}</div>
                {!dashboardDraft.length && <p className='general-settings-empty'>No dashboard links are configured. Add a link to get started.</p>}
                <div className='general-settings-modal-actions'><button type='button' disabled={saving} onClick={() => setDashboardModalOpen(false)}>Cancel</button><button className='general-settings-primary' type='button' disabled={saving} onClick={saveDashboard}>{saving ? 'Saving...' : 'Save dashboard links'}</button></div>
            </section>
        </div>}
    </div></div>;
}
